import {
  PdfGen,
  PdfUnit,
  PdfSize,
  PdfRect,
  PdfPoint,
  PdfBrush,
  PdfFont,
  ETextAlign,
} from '../../pdf/pdf';
import { ILayoutLabelTextGen } from '../layoutLabelText';
import { OptsLabelText } from '../optsLabelText';
import { GUtil } from '../../pdf/gutil';
import { Random, IItemCount } from '@educraft/base';
import '../../pdf/draw/primitives';
import '../../pdf/draw/string';
import '../../pdf/draw/label';
import '../../pdf/draw/helpers';
import { calcGridRectsEx } from '../../pdf/board/grid';

interface Solution {
  word: string;
  row: number;
  col: number;
  dc: number;
  dr: number;
}

/**
 * Osmismerka__3 (Word Search) - Find hidden words in a letter grid (Original 3-direction algorithm)
 */
export class Osmismerka__3 implements ILayoutLabelTextGen, IItemCount {
  countDefault(): number {
    return 10;
  }
  countMin(): number {
    return 1;
  }
  countMax(): number {
    return 25;
  }

  public async generate(pdf: PdfGen, opts: OptsLabelText): Promise<void> {
    const margin = PdfUnit.FromMillimeters(20);
    const count = Math.min(opts.list.length, this.countMax());
    const items = opts.list.slice(0, count);
    const words = items.map((t) => t.text);

    let gridSize = Math.max(...words.map((w) => w.replace(/ /g, '').replace(/_/g, '').length)) + 1;

    const ws = new WordSearch__3(opts.data.random, gridSize, words);
    const grid = ws.build();
    gridSize = ws.gridSize;

    const letterSize =
      Math.min(PdfSize.A4.width - 2 * margin, PdfSize.A4.height / 2 - margin) / gridSize;
    const font = new PdfFont(PdfFont.FONT_NAME, letterSize * 0.8);

    const startX = (PdfSize.A4.width - gridSize * letterSize) / 2;
    const startY = PdfSize.A4.height - margin - gridSize * letterSize;

    const page1 = pdf.addPage();
    const page2 = pdf.addPage();
    const page3 = pdf.addPage();

    for (let i = 0; i < gridSize; i++) {
      for (let j = 0; j < gridSize; j++) {
        const rect = new PdfRect(
          startX + i * letterSize,
          startY + j * letterSize,
          letterSize,
          letterSize
        );
        const ch = grid[i][j];
        page1.drawRectangle(GUtil.basePen, null, rect);
        page1.drawFillStringCenter(ch, font, PdfBrush.Black, rect);
        page2.drawRectangle(GUtil.basePen, null, rect);
        page2.drawFillStringCenter(ch, font, PdfBrush.Black, rect);
      }
    }

    const topArea = new PdfRect(
      margin,
      margin,
      PdfSize.A4.width - 2 * margin,
      PdfSize.A4.height / 2 - 2 * margin
    );
    const grid1 = calcGridRectsEx(count, topArea, 2);

    for (let i = 0; i < count; i++) {
      page1.drawLabel(items[i].label, { rect: grid1.rects[i] });
    }

    const textHeight = Math.min(PdfUnit.FromMillimeters(10), grid1.cellSize.height * 0.25);
    const labelFont = page2.adjustFontMulti(
      items.map((t) => [t.text]),
      GUtil.baseFont,
      new PdfSize(grid1.cellSize.width, textHeight)
    );

    for (let i = 0; i < count; i++) {
      const cell = grid1.rects[i];
      const imgR = new PdfRect(cell.x, cell.y, cell.width, cell.height - textHeight);
      const labelR = new PdfRect(cell.x, cell.y + cell.height - textHeight, cell.width, textHeight);
      page2.drawLabel(items[i].label, { rect: imgR });
      page2.drawFillStringCenter(items[i].text, labelFont, PdfBrush.Black, labelR);
    }

    const hintSize = PdfUnit.FromMillimeters(10);
    const hintFont = new PdfFont(PdfFont.FONT_NAME, 12);

    for (let i = 0; i < gridSize; i++) {
      for (let j = 0; j < gridSize; j++) {
        const rect = new PdfRect(margin + i * hintSize, margin + j * hintSize, hintSize, hintSize);
        page3.drawRectangle(GUtil.basePen, null, rect);
        page3.drawFillStringCenter(grid[i][j], hintFont, PdfBrush.Black, rect);
      }
    }

    for (const sol of ws.solutions) {
      const p1 = new PdfPoint(
        margin + sol.row * hintSize + hintSize / 2,
        margin + sol.col * hintSize + hintSize / 2
      );
      const p2 = new PdfPoint(
        p1.x + (sol.word.length - 1) * hintSize * sol.dr,
        p1.y + (sol.word.length - 1) * hintSize * sol.dc
      );
      page3.drawLine(GUtil.basePen, p1, p2);
    }
  }
}

/**
 * Osmismerka__8 (Word Search) - Find hidden words in a letter grid (Advanced 8-direction algorithm)
 */
export class Osmismerka__8 implements ILayoutLabelTextGen, IItemCount {
  countDefault(): number {
    return 10;
  }
  countMin(): number {
    return 1;
  }
  countMax(): number {
    return 25;
  }

  public async generate(pdf: PdfGen, opts: OptsLabelText): Promise<void> {
    const margin = PdfUnit.FromMillimeters(20);
    const count = Math.min(opts.list.length, this.countMax());
    const items = opts.list.slice(0, count);
    const words = items.map((t) => t.text);

    let gridSize = Math.max(...words.map((w) => w.replace(/ /g, '').replace(/_/g, '').length));

    const ws = new WordSearch__8(opts.data.random, gridSize, words);
    const grid = ws.build();
    gridSize = ws.gridSize;

    const letterSize =
      Math.min(PdfSize.A4.width - 2 * margin, PdfSize.A4.height / 2 - margin) / gridSize;
    const font = new PdfFont(PdfFont.FONT_NAME, letterSize * 0.8);

    const startX = (PdfSize.A4.width - gridSize * letterSize) / 2;
    const startY = PdfSize.A4.height - margin - gridSize * letterSize;

    const page1 = pdf.addPage();
    const page2 = pdf.addPage();
    const page3 = pdf.addPage();

    for (let i = 0; i < gridSize; i++) {
      for (let j = 0; j < gridSize; j++) {
        const rect = new PdfRect(
          startX + i * letterSize,
          startY + j * letterSize,
          letterSize,
          letterSize
        );
        const ch = grid[i][j];
        page1.drawRectangle(GUtil.basePen, null, rect);
        page1.drawFillStringCenter(ch, font, PdfBrush.Black, rect);
        page2.drawRectangle(GUtil.basePen, null, rect);
        page2.drawFillStringCenter(ch, font, PdfBrush.Black, rect);
      }
    }

    const topArea = new PdfRect(
      margin,
      margin,
      PdfSize.A4.width - 2 * margin,
      PdfSize.A4.height / 2 - 2 * margin
    );
    const grid1 = calcGridRectsEx(count, topArea, 2);

    for (let i = 0; i < count; i++) {
      page1.drawLabel(items[i].label, { rect: grid1.rects[i] });
    }

    const textHeight = Math.min(PdfUnit.FromMillimeters(10), grid1.cellSize.height * 0.25);
    const labelFont = page2.adjustFontMulti(
      items.map((t) => [t.text]),
      GUtil.baseFont,
      new PdfSize(grid1.cellSize.width, textHeight)
    );

    for (let i = 0; i < count; i++) {
      const cell = grid1.rects[i];
      const imgR = new PdfRect(cell.x, cell.y, cell.width, cell.height - textHeight);
      const labelR = new PdfRect(cell.x, cell.y + cell.height - textHeight, cell.width, textHeight);
      page2.drawLabel(items[i].label, { rect: imgR });
      page2.drawFillStringCenter(items[i].text, labelFont, PdfBrush.Black, labelR);
    }

    const hintSize = PdfUnit.FromMillimeters(10);
    const hintFont = new PdfFont(PdfFont.FONT_NAME, 12);

    for (let i = 0; i < gridSize; i++) {
      for (let j = 0; j < gridSize; j++) {
        const rect = new PdfRect(margin + i * hintSize, margin + j * hintSize, hintSize, hintSize);
        page3.drawRectangle(GUtil.basePen, null, rect);
        page3.drawFillStringCenter(grid[i][j], hintFont, PdfBrush.Black, rect);
      }
    }

    for (const sol of ws.solutions) {
      const p1 = new PdfPoint(
        margin + sol.row * hintSize + hintSize / 2,
        margin + sol.col * hintSize + hintSize / 2
      );
      const p2 = new PdfPoint(
        p1.x + (sol.word.length - 1) * hintSize * sol.dr,
        p1.y + (sol.word.length - 1) * hintSize * sol.dc
      );
      page3.drawLine(GUtil.basePen, p1, p2);
    }
  }
}

class WordSearch__3 {
  private grid: string[][] = [];
  public gridSize: number;
  private words: string[];
  public solutions: Solution[] = [];
  private random: Random;

  constructor(random: Random, size: number, wordsToPlace: string[]) {
    this.random = random;
    this.gridSize = size;
    this.words = wordsToPlace
      .map((w) => w.replace(/ /g, '').replace(/_/g, ''))
      .sort((a, b) => b.length - a.length)
      .map((w) => w.toUpperCase());
  }

  public build(): string[][] {
    let errors = 0;
    while (true) {
      try {
        this.grid = Array.from({ length: this.gridSize }, () =>
          Array.from({ length: this.gridSize }, () => '')
        );
        this.solutions = [];
        this.placeWords();
        break;
      } catch {
        if (errors++ > 50) {
          errors = 0;
          this.gridSize++;
        }
      }
    }
    this.fillRandom();
    return this.grid;
  }

  private fillRandom(): void {
    for (let i = 0; i < this.gridSize; i++) {
      for (let j = 0; j < this.gridSize; j++) {
        if (!this.grid[i][j]) {
          this.grid[i][j] = String.fromCharCode(65 + this.random.next(0, 26));
        }
      }
    }
  }

  private placeWords(): void {
    let retries = 0;
    for (const word of this.words) {
      let placed = false;
      while (!placed) {
        const row = this.random.next(0, this.gridSize);
        const col = this.random.next(0, this.gridSize);
        const direction = this.random.next(0, 3);
        if (this.canPlace(word, row, col, direction)) {
          this.place(word, row, col, direction);
          placed = true;
        }
        if (retries++ > 100000) throw new Error('Cannot place words');
      }
    }
  }

  private canPlace(word: string, row: number, col: number, direction: number): boolean {
    let dr = 0,
      dc = 0;
    if (direction === 0) dc = 1;
    else if (direction === 1) dr = 1;
    else {
      dr = 1;
      dc = 1;
    }

    for (let i = 0; i < word.length; i++) {
      const nr = row + dr * i;
      const nc = col + dc * i;
      if (nr < 0 || nc < 0 || nr >= this.gridSize || nc >= this.gridSize) return false;
      if (this.grid[nr][nc] && this.grid[nr][nc] !== word[i]) return false;
    }
    return true;
  }

  private place(word: string, row: number, col: number, direction: number): void {
    let dr = 0,
      dc = 0;
    if (direction === 0) dc = 1;
    else if (direction === 1) dr = 1;
    else {
      dr = 1;
      dc = 1;
    }
    for (let i = 0; i < word.length; i++) {
      this.grid[row + dr * i][col + dc * i] = word[i];
    }
    this.solutions.push({ word, row, col, dc, dr });
  }
}

class WordSearch__8 {
  private grid: string[][] = [];
  public gridSize: number;
  private words: string[];
  public solutions: Solution[] = [];
  private random: Random;

  private static readonly DIRECTIONS: [number, number][] = [
    [1, 0],
    [1, 1],
    [0, 1],
    [-1, 1],
    [-1, 0],
    [-1, -1],
    [0, -1],
    [1, -1],
  ];

  constructor(random: Random, size: number, wordsToPlace: string[]) {
    this.random = random;
    this.gridSize = size;
    this.words = wordsToPlace
      .map((w) => w.replace(/ /g, '').replace(/_/g, ''))
      .sort((a, b) => b.length - a.length)
      .map((w) => w.toUpperCase());
  }

  public build(): string[][] {
    let attemptsAtThisSize = 0;
    while (true) {
      try {
        this.grid = Array.from({ length: this.gridSize }, () =>
          Array.from({ length: this.gridSize }, () => '')
        );
        this.solutions = [];
        this.placeWords();
        break;
      } catch {
        if (attemptsAtThisSize++ > 50) {
          attemptsAtThisSize = 0;
          this.gridSize++;
        }
      }
    }
    this.fillRandom();
    return this.grid;
  }

  private fillRandom(): void {
    for (let i = 0; i < this.gridSize; i++) {
      for (let j = 0; j < this.gridSize; j++) {
        if (!this.grid[i][j]) {
          this.grid[i][j] = String.fromCharCode(65 + this.random.next(0, 26));
        }
      }
    }
  }

  private placeWords(): void {
    for (const word of this.words) {
      const bestPositions: {
        row: number;
        col: number;
        dr: number;
        dc: number;
        overlaps: number;
      }[] = [];
      let maxOverlaps = -1;

      for (let r = 0; r < this.gridSize; r++) {
        for (let c = 0; c < this.gridSize; c++) {
          for (const [dr, dc] of WordSearch__8.DIRECTIONS) {
            const overlaps = this.checkPlacement(word, r, c, dr, dc);
            if (overlaps !== null) {
              if (overlaps > maxOverlaps) {
                maxOverlaps = overlaps;
                bestPositions.length = 0;
                bestPositions.push({ row: r, col: c, dr, dc, overlaps });
              } else if (overlaps === maxOverlaps) {
                bestPositions.push({ row: r, col: c, dr, dc, overlaps });
              }
            }
          }
        }
      }

      if (bestPositions.length === 0) {
        throw new Error(`Cannot place word: ${  word}`);
      }

      const pick = this.random.nextElement(bestPositions);
      this.place(word, pick.row, pick.col, pick.dr, pick.dc);
    }
  }

  private checkPlacement(
    word: string,
    row: number,
    col: number,
    dr: number,
    dc: number
  ): number | null {
    let overlaps = 0;
    for (let i = 0; i < word.length; i++) {
      const nr = row + dr * i;
      const nc = col + dc * i;
      if (nr < 0 || nc < 0 || nr >= this.gridSize || nc >= this.gridSize) return null;
      const existing = this.grid[nr][nc];
      if (existing) {
        if (existing !== word[i]) return null;
        overlaps++;
      }
    }
    return overlaps;
  }

  private place(word: string, row: number, col: number, dr: number, dc: number): void {
    for (let i = 0; i < word.length; i++) {
      this.grid[row + dr * i][col + dc * i] = word[i];
    }
    this.solutions.push({ word, row, col, dc, dr });
  }
}
